import { App, Avatar, Drawer, Empty, Input, Spin, Tooltip } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { UserRoleEnum } from '../../../enums/UserRoleEnum';
import { useOnBehalfOf } from '../../../hooks/useOnBehalfOf';
import type User from '../../../interfaces/User';
import type { GetUsersPayload } from '../../../interfaces/UserApiService';
import { UserService } from '../../../services/userApiService';
import { getAvatarColor } from '../../../utils/avatarColors';
import { getUserInitials, getUserName, getUserRoleIconName } from '../../../utils/utils';
import { Icon } from '../../Core/Icon';
import EnterOnBehalfOfConfirmationForm from '../../Forms/EnterOnBehalfOfConfirmationForm';
import TruncatedTooltip from '../TruncatedTooltip';

const userService = new UserService();

interface OnBehalfOfDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const OnBehalfOfDrawer = ({ open, onClose }: OnBehalfOfDrawerProps) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { enterOnBehalfOfUser } = useOnBehalfOf();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [userFilters, setUserFilters] = useState<GetUsersPayload>({
    count: 25,
    offset: 0,
    search: '',
    roleFilter: [UserRoleEnum.Contributor, UserRoleEnum.Reader, UserRoleEnum.External],
    ignoreBehalf: false,
  });
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Retrieves a list of users based on the provided filters
   */
  const getUsers = useCallback(async (newFilters: GetUsersPayload, reset = false) => {
    setIsLoading(true);
    try {
      const res = await userService.getUsers(newFilters);
      const result = res?.users || [];

      if (reset) {
        setUsers(result);
      } else {
        setUsers((prevUsers) => [...prevUsers, ...result]);
      }
      setTotalUsers(res?.total || 0);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Load initial users when drawer opens
   */
  useEffect(() => {
    if (open) {
      setUsers([]);
      setUserFilters({
        count: 25,
        offset: 0,
        search: '',
        roleFilter: [UserRoleEnum.Contributor, UserRoleEnum.Reader, UserRoleEnum.External],
        ignoreBehalf: false,
      });
      setSearchValue('');
      void getUsers(
        {
          count: 25,
          offset: 0,
          search: '',
          roleFilter: [UserRoleEnum.Contributor, UserRoleEnum.Reader, UserRoleEnum.External],
          ignoreBehalf: false,
        },
        true,
      );
    }
  }, [open, getUsers]);

  /**
   * Handle search input with debounce
   */
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const timer = setTimeout(() => {
        const newFilters = {
          ...userFilters,
          offset: 0,
          search: value,
        };
        setUserFilters(newFilters);
        void getUsers(newFilters, true);
      }, 300);

      setDebounceTimer(timer);
    },
    [debounceTimer, userFilters, getUsers],
  );

  /**
   * Handle infinite scroll
   */
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoading || users.length >= totalUsers) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      // Load more users
      const newFilters = {
        ...userFilters,
        offset: userFilters.offset + userFilters.count,
      };
      setUserFilters(newFilters);
      void getUsers(newFilters, false);
    }
  }, [isLoading, users.length, totalUsers, userFilters, getUsers]);

  /**
   * Handle user click - show confirmation modal with reason form
   */
  const handleUserClick = useCallback(
    (user: User) => {
      const userName = getUserName(user.firstName, user.lastName, user.email);

      const modalInstance = modal.confirm({
        title: t('modal.impersonateUser.title') || 'Impersonate User',
        content: (
          <EnterOnBehalfOfConfirmationForm
            content={
              <Trans>
                {t('modal.impersonateUser.description', {
                  userName,
                })}
              </Trans>
            }
            onSubmit={async (reason: string) => {
              try {
                await enterOnBehalfOfUser(user.id, reason);
                modalInstance.destroy();
                onClose();
              } catch (error) {
                // Error already handled in hook
              }
            }}
            onCancel={() => {
              modalInstance.destroy();
            }}
          />
        ),
        footer: null,
      });
    },
    [enterOnBehalfOfUser, onClose, modal, t],
  );

  return (
    <Drawer
      title={t('common.impersonateUser')}
      placement="right"
      onClose={onClose}
      open={open}
      size={500}
    >
      <div className="flex h-full flex-col">
        {/* Search input */}
        <div className="mb-4">
          <Input
            size="large"
            placeholder={t('common.search') || 'Search users...'}
            prefix={<Icon name="SearchIcon" className="h-4 w-4" />}
            suffix={isLoading ? <Spin size="small" /> : null}
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
          />
        </div>

        {/* Users list */}
        <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto">
          {isLoading && users.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Spin size="large" />
            </div>
          ) : users.length === 0 ? (
            <Empty description={t('common.noUsersFound')} className="py-8" />
          ) : (
            <ol className="list-none space-y-2 p-0">
              {users.map((user) => {
                const userName = getUserName(user.firstName, user.lastName, user.email);
                const initials = getUserInitials(user.firstName, user.lastName, user.email);

                return (
                  <li key={user.id} className="list-none">
                    <button
                      type="button"
                      aria-label={t('a11y.selectUser', { name: userName })}
                      onClick={() => handleUserClick(user)}
                      className="hover:bg-gray-light flex w-full cursor-pointer items-center gap-3 rounded-md border-0 bg-transparent p-3 text-left transition-colors"
                    >
                      <Avatar
                        size={40}
                        className="text-blue-dark! flex! shrink-0! items-center justify-center! uppercase!"
                        style={{ backgroundColor: getAvatarColor(user.id) }}
                      >
                        {user.firstName || user.lastName ? (
                          <span className="font-medium">{initials}</span>
                        ) : (
                          <Icon name="AtSignIcon" className="h-5 w-5" />
                        )}
                      </Avatar>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="text-blue-dark truncate font-medium">
                          <TruncatedTooltip>{userName}</TruncatedTooltip>
                        </span>
                        <div className="flex flex-row items-center gap-1">
                          <Tooltip title={t(`roles.${user.role}`)}>
                            <div>
                              <Icon
                                name={getUserRoleIconName(user.role)}
                                className="text-gray-dark h-4 w-4"
                              />
                            </div>
                          </Tooltip>
                          {user.email && (
                            <span className="text-gray-dark truncate text-sm">
                              <TruncatedTooltip> • {user.email}</TruncatedTooltip>
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}

          {isLoading && users.length > 0 && (
            <div className="flex items-center justify-center py-4">
              <Spin />
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};
